import React, { useState } from 'react';
import Button from '../common/Button';
import { Caption } from '../common/Typography';

export const GUEST_LINK_ACTIONS = {
  MERGE: 'merge',
  START_FRESH: 'start_fresh',
  DECLINE: 'decline',
};

const CLUB_JOIN_OTHER_OPTIONS = [
  {
    id: GUEST_LINK_ACTIONS.START_FRESH,
    title: 'Start fresh',
    description: 'Join without linking.',
  },
  {
    id: GUEST_LINK_ACTIONS.DECLINE,
    title: "This isn't my account",
    description: 'Report wrong email to admins.',
    danger: true,
  },
];

const ACCOUNT_LINK_OTHER_OPTIONS = [
  {
    id: GUEST_LINK_ACTIONS.DECLINE,
    title: "This isn't my account",
    description: 'Report wrong email to admins.',
    danger: true,
  },
];

const VARIANT_CONFIG = {
  clubJoin: {
    mergeLabel: 'Link & join',
    loadingLabel: 'Linking…',
    caption: 'Keeps your games, ELO, and partnerships — you join as a club member.',
    otherOptions: CLUB_JOIN_OTHER_OPTIONS,
  },
  accountLink: {
    mergeLabel: 'Review & link',
    loadingLabel: 'Loading…',
    caption: 'Brings your game history and ratings into your account.',
    otherOptions: ACCOUNT_LINK_OTHER_OPTIONS,
  },
};

/**
 * Primary merge CTA with uncommon paths tucked under "Other options".
 */
export default function GuestLinkActionChooser({
  variant = 'clubJoin',
  compact = false,
  secondaryAction = null,
  onMerge,
  onStartFresh,
  onDecline,
  disabled = false,
  loading = false,
}) {
  const [showOther, setShowOther] = useState(false);
  const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.clubJoin;
  const otherOptions = config.otherOptions;

  return (
    <div className={compact ? 'space-y-2' : 'space-y-4'}>
      <div
        className={
          compact
            ? 'space-y-1.5'
            : 'rounded-xl border border-primary/20 bg-primary/5 p-3 sm:p-3.5'
        }
      >
        <div
          className={
            secondaryAction
              ? 'flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center'
              : undefined
          }
        >
          <Button
            type="button"
            variant="primary"
            size={compact ? 'small' : 'medium'}
            className={
              secondaryAction
                ? compact
                  ? 'w-full sm:w-auto sm:min-w-[10rem]'
                  : 'w-full shadow-brand-sm sm:w-auto'
                : compact
                  ? 'w-full sm:w-auto sm:min-w-[10rem]'
                  : 'w-full shadow-brand-sm'
            }
            disabled={disabled || loading}
            onClick={onMerge}
          >
            {loading ? config.loadingLabel : config.mergeLabel}
          </Button>
          {secondaryAction ? (
            <Button
              type="button"
              variant="outline"
              size={compact ? 'small' : 'medium'}
              className={compact ? 'w-full sm:w-auto' : 'w-full sm:w-auto'}
              disabled={secondaryAction.disabled || loading}
              onClick={secondaryAction.onClick}
            >
              {secondaryAction.label}
            </Button>
          ) : null}
        </div>
        <Caption
          className={
            compact
              ? 'block leading-snug text-text-muted'
              : 'mt-2 block text-center leading-snug max-w-[280px] mx-auto'
          }
        >
          {config.caption}
        </Caption>
      </div>

      {otherOptions.length > 0 ? (
      <div className={compact ? '' : 'pt-1'}>
        <button
          type="button"
          disabled={disabled || loading}
          onClick={() => setShowOther((open) => !open)}
          className="flex w-full items-center justify-between gap-2 rounded-md py-1 text-xs font-medium text-text-muted hover:text-text-secondary disabled:opacity-50"
          aria-expanded={showOther}
        >
          <span>Other options</span>
          <svg
            className={`h-3.5 w-3.5 shrink-0 transition-transform ${showOther ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {showOther ? (
          <div className="mt-1.5 space-y-1" role="group" aria-label="Other ways to join">
            {otherOptions.map((option) => (
              <button
                key={option.id}
                type="button"
                disabled={disabled || loading}
                onClick={option.id === GUEST_LINK_ACTIONS.START_FRESH ? onStartFresh : onDecline}
                className={`w-full rounded-lg border px-3 py-2 text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  option.danger
                    ? 'border-danger/25 bg-surface hover:bg-danger-light/40'
                    : 'border-border-light bg-surface hover:bg-surface-subtle'
                }`}
              >
                <span
                  className={`block text-sm font-medium ${
                    option.danger ? 'text-danger' : 'text-text-primary'
                  }`}
                >
                  {option.title}
                </span>
                <Caption className="mt-0.5 block">{option.description}</Caption>
              </button>
            ))}
          </div>
        ) : null}
      </div>
      ) : null}
    </div>
  );
}
